import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { arrToNames } from '../constants/exploreFilterEngine';

/**
 * Controlled-list multi-select filter — checkbox list with frequency counts.
 * Used for Situation in V1. OR semantics within the column.
 *
 * Tag fields like `situation` are `Array<{name,...}>` per the data model;
 * arrToNames flattens both shapes.
 */
const FilterMultiSelect = observer(({ columnKey, value, onChange, results }) => {
  const items = useMemo(() => {
    const counts = new Map();
    for (const c of results) {
      const names = arrToNames(c?.[columnKey]);
      for (const n of names) {
        counts.set(n, (counts.get(n) || 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [results, columnKey]);

  const selected = value?.values || new Set();

  const toggle = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    if (next.size === 0) onChange(null);
    else onChange({ values: next });
  };

  return (
    <div className="filter-multi-select">
      <div className="filter-help">Multi-select · matches any selected</div>
      <div className="filter-checkbox-list">
        {items.length === 0 ? (
          <div className="filter-empty">No values in current results.</div>
        ) : (
          items.map(([name, count]) => (
            <label key={name} className="filter-checkbox-row">
              <input
                type="checkbox"
                checked={selected.has(name)}
                onChange={() => toggle(name)}
              />
              <span className="filter-checkbox-label">{name}</span>
              <span className="filter-checkbox-count">{count.toLocaleString('en-US')}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
});

FilterMultiSelect.propTypes = {
  columnKey: PropTypes.string.isRequired,
  value: PropTypes.shape({
    values: PropTypes.object // Set<string>
  }),
  onChange: PropTypes.func.isRequired,
  results: PropTypes.array.isRequired
};

export default FilterMultiSelect;
